import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Icon } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm, DATE_FORMATS_FNS, ReactTable, useCan, useSelectedDealer } from "components";
import { useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES, PUBLIC_ACCOUNT_STATUSES } from "models";
import { LogsModal } from "modules/AccessKeysDashboard/components";
import "modules/AccessKeysDashboard/components/AccessKeysDashboardTable/AccessKeysDashboardTable.scss";
import { useAccountsQuery, useLogsQuery, useRequestApiKeyMutation, useRevokeAccountMutation } from "modules/AccessKeysDashboard/hooks";
import { copyToClipboard } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { PublicAccountWithApiKey } from "modules/AccessKeysDashboard/queryKeys";

const columnHelper = createColumnHelper<PublicAccountWithApiKey>();

export const AccessKeysDashboardTable = () => {
  const { t } = useTranslation();

  const { selectedDealer } = useSelectedDealer();
  const canRequestLogs = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.PublicAPILogs);
  const canRevokeApiKey = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.PublicAPIAccount);
  const canRequestApiKey = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.PublicAPIKeys);
  const canListAccounts = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.PublicAPIAccount);

  const { mutateAsync: revokeAccount } = useRevokeAccountMutation();
  const { mutateAsync: requestApiKey } = useRequestApiKeyMutation();
  const { data: accounts = [], isLoading: accountsLoading, refetch: refetchAccounts, isFetching: accountsFetching } = useAccountsQuery();

  const [apiKeyConfirmationNote, setApiKeyConfirmationNote] = useState("");
  const [apiKeyModalId, setApiKeyModalId] = useState<number | null>(null);
  const [revokeKeyModalId, setRevokeKeyModalId] = useState<number | null>(null);
  const [requestLogsPublicAccountId, setRequestLogsPublicAccountId] = useState<number | null>(null);

  const { data: logs, refetch: refetchLogs, isFetching } = useLogsQuery(requestLogsPublicAccountId);
  const logsTitle = accounts.find(account => account.id === requestLogsPublicAccountId)?.name ?? "";

  useRegisterNavAction(() => {
    if (canListAccounts) refetchAccounts();
  });

  useEffect(() => {
    if (canRequestLogs && requestLogsPublicAccountId) refetchLogs();
  }, [requestLogsPublicAccountId, selectedDealer]);

  const handleCloseRequestLogsModal = () => setRequestLogsPublicAccountId(null);
  const handleCopyToClipboard = (content: string, message: string) => copyToClipboard(content).then(() => toast.success(message));

  const handleRequestApiKeyModalReset = () => {
    setApiKeyConfirmationNote("");
    setApiKeyModalId(null);
  };

  const handleRequestApiKey = async () => {
    if (!apiKeyModalId) return;
    await requestApiKey({ public_account_id: apiKeyModalId, reason: apiKeyConfirmationNote });
    handleRequestApiKeyModalReset();
  };

  const handleRevokeApiKey = async () => {
    if (!revokeKeyModalId) return;
    await revokeAccount(revokeKeyModalId);
    setRevokeKeyModalId(null);
  };

  const columns = [
    columnHelper.accessor(row => row, {
      id: "name",
      header: () => <span className="no-wrap-text">{t("v8_name", "Name")}</span>,
      size: 200,
      cell: info => <span className="no-wrap-text">{info.row.original.name}</span>
    }),

    columnHelper.accessor(row => row, {
      id: "key",
      header: () => <span className="no-wrap-text">{t("v8_key", "Key")}</span>,
      size: 80,
      cell: ({
        row: {
          original: { status, api_key, id }
        }
      }) => {
        if (status === PUBLIC_ACCOUNT_STATUSES.PublicAccountRevoked) return "";

        if (api_key) {
          return (
            <div
              className="flex AccessKeysDashboardTable__copy-to-clipboard"
              onClick={() => handleCopyToClipboard(api_key || "", t("v8_api_key_copied", "Api key copied to clipboard"))}
            >
              <div className="AccessKeysDashboardTable__api-key-container">
                <Icon name="copy outline" className="AccessKeysDashboardTable__copy-icon green" />
                <span>{api_key}</span>
              </div>
            </div>
          );
        }

        return (
          <button disabled={!canRequestApiKey} className="AccessKeysDashboardTable__button AccessKeysDashboardTable__button-teal" onClick={() => setApiKeyModalId(id)}>
            <span className="no-wrap-text">{t("v8_show_the_api_key", "Show the API Key")} </span>
          </button>
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "status",
      header: () => <span className="no-wrap-text">{t("v8_status", "Status")}</span>,
      size: 80,
      cell: ({
        row: {
          original: { status, id }
        }
      }) => {
        if (status === PUBLIC_ACCOUNT_STATUSES.PublicAccountRevoked) {
          return (
            <button className="AccessKeysDashboardTable__button AccessKeysDashboardTable__button-disabled">
              <span className="no-wrap-text">{t("v8_revoked", "Revoked")} </span>
            </button>
          );
        }

        return (
          <button
            disabled={!canRevokeApiKey}
            className="AccessKeysDashboardTable__button AccessKeysDashboardTable__button-orange"
            onClick={() => setRevokeKeyModalId(id)}
          >
            <span className="no-wrap-text">{t("v8_revoke", "Revoke")} </span>
          </button>
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "created-by",
      header: () => <span className="no-wrap-text">{t("v8_created_by", "Created By")}</span>,
      size: 100,
      cell: info => <span className="no-wrap-text"> {info.row.original.created_by_name} </span>
    }),

    columnHelper.accessor(row => row, {
      id: "created-on",
      header: () => <span className="no-wrap-text">{t("v8_created_on", "Created On")}</span>,
      size: 80,
      cell: info => <span className="no-wrap-text"> {formatDate(info.row.original.created_on, DATE_FORMATS_FNS.dateMonthYear)} </span>
    }),

    columnHelper.accessor(row => row, {
      id: "contact",
      header: () => <span className="no-wrap-text">{t("v8_contact", "Contact")}</span>,
      size: 450,
      cell: info => <span className="no-wrap-text"> {info.row.original.contact} </span>
    }),

    columnHelper.accessor(row => row, {
      id: "last-used",
      header: () => <span className="no-wrap-text">{t("v8_last_used", "Last Used")}</span>,
      size: 60,
      cell: ({
        row: {
          original: { last_used_at }
        }
      }) => {
        if (!last_used_at) return <span className="no-wrap-text"> {t("v8_not_used_yet", "Not used yet")} </span>;

        return <span className="no-wrap-text"> {formatDate(last_used_at, DATE_FORMATS_FNS.dateMonthYear)} </span>;
      }
    }),

    columnHelper.accessor(row => row, {
      id: "request-logs",
      header: () => <span className="no-wrap-text request-logs-header">{t("v8_request_logs", "Request Logs")}</span>,
      size: 120,
      cell: info => (
        <div className="request-logs-button-container">
          <button
            disabled={!canRequestLogs}
            className="AccessKeysDashboardTable__button AccessKeysDashboardTable__button-teal"
            onClick={() => setRequestLogsPublicAccountId(info.row.original.id)}
          >
            <span className="no-wrap-text">{t("v8_request_logs", "Request Logs")} </span>
          </button>
        </div>
      )
    })
  ];

  return (
    <>
      <div className="AccessKeysDashboardTable">
        <ReactTable isFetching={accountsLoading || accountsFetching} columns={columns} data={accounts} />
      </div>

      <CustomConfirm
        note={apiKeyConfirmationNote}
        type={CUSTOM_CONFIRM_TYPES.Warning}
        isOpen={Boolean(apiKeyModalId)}
        handleConfirm={handleRequestApiKey}
        handleCancel={handleRequestApiKeyModalReset}
        onChangeNote={e => setApiKeyConfirmationNote(e.target.value)}
        customTitle={t("v8_confirmation_required", "Confirmation required")}
        confirmMandatoryNote={t("v8_reason_for_requesting_api_key", "Reason for requesting API key.")}
        confirmMsg={t("v8_access_to_api_key_is_logged_give_reason_for_displaying_it", "Access to API key is logged, please give the reason for displaying it.")}
      />
      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Warning}
        isOpen={Boolean(revokeKeyModalId)}
        handleConfirm={handleRevokeApiKey}
        handleCancel={() => setRevokeKeyModalId(null)}
        customTitle={t("v8_confirmation_required", "Confirmation required")}
        confirmMsg={
          t("v8_are_you_sure_you_want_to_revoke_this_key_once_revoked_it_can_not_be_activated_again") ||
          "Are you sure you want to revoke this key? Once revoked it can not be activated again."
        }
      />
      <LogsModal logs={logs} isFetching={isFetching} onClose={handleCloseRequestLogsModal} open={Boolean(requestLogsPublicAccountId)} title={logsTitle} />
    </>
  );
};
